import { useState, useEffect } from "react";
import {
  CheckCircle2,
  ExternalLink,
  FileSpreadsheet,
  Mail,
  Moon,
  Monitor,
  Server,
  Shield,
  Sun,
} from "lucide-react";
import { authGetStatus, getGoogleIntegration, getXeroIntegration } from "../gen";
import { useOrg } from "../hooks/useOrg";
import { useRole } from "../hooks/useRole";
import { useTheme } from "../hooks/useTheme";

export function SettingsPage() {
  const { activeOrgId, activeOrgName } = useOrg();
  const { role, user } = useRole();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [status, setStatus] = useState<any>(null);
  const [googleIntegration, setGoogleIntegration] = useState<any>(null);
  const [xeroIntegration, setXeroIntegration] = useState<any>(null);

  useEffect(() => {
    authGetStatus()
      .then((res) => setStatus((res as any).data))
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    if (user?.email) {
      getGoogleIntegration({ email: user.email })
        .then((res) => {
          if (res.status === 200 && (res as any).data) {
            setGoogleIntegration((res as any).data);
          } else {
            setGoogleIntegration(null);
          }
        })
        .catch(() => setGoogleIntegration(null));
    }
  }, [user?.email]);

  useEffect(() => {
    if (activeOrgId) {
      getXeroIntegration()
        .then((res) => {
          if (res.status === 200 && (res as any).data) {
            setXeroIntegration((res as any).data);
          } else {
            setXeroIntegration(null);
          }
        })
        .catch(() => setXeroIntegration(null));
    }
  }, [activeOrgId]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="border-b border-stone-200 dark:border-stone-800 pb-5">
        <h1 className="text-2xl font-bold tracking-tight text-stone-900 dark:text-stone-100">
          Platform Settings & Diagnostics
        </h1>
        <p className="text-stone-500 dark:text-stone-400 text-sm mt-1">
          Review tenant organization scope, appearance preferences, backend API configuration, and authentication status.
        </p>
      </div>

      <div className="space-y-6">
        {/* Appearance & Color Scheme Preference */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800/80 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
              <Sun className="w-4 h-4 text-amber-500" />
              <span>Appearance & Color Theme</span>
            </h3>
            <span className="text-xs font-medium text-stone-500 dark:text-stone-400">
              Active: <strong className="text-stone-800 dark:text-stone-200 capitalize">{resolvedTheme}</strong>
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              {
                id: "system" as const,
                label: "System Default",
                desc: `Auto (${resolvedTheme === "dark" ? "Dark" : "Light"})`,
                icon: Monitor,
              },
              {
                id: "light" as const,
                label: "Light Theme",
                desc: "Crisp daylight",
                icon: Sun,
              },
              {
                id: "dark" as const,
                label: "Dark Theme",
                desc: "Deep obsidian",
                icon: Moon,
              },
            ].map((item) => {
              const Icon = item.icon;
              const isSelected = theme === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTheme(item.id)}
                  className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                    isSelected
                      ? "border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-800/80"
                      : "border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:bg-stone-50/75 dark:hover:bg-stone-800/40"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 mt-0.5 shrink-0 ${
                      isSelected
                        ? "text-stone-900 dark:text-stone-100"
                        : "text-stone-400 dark:text-stone-500"
                    }`}
                  />
                  <div>
                    <span className="text-xs font-semibold text-stone-900 dark:text-stone-100 block">
                      {item.label}
                    </span>
                    <span className="text-[11px] text-stone-400 dark:text-stone-500 block">
                      {item.desc}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tenant Organization Context Card */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800/80 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Multi-Tenant Organization Scope</span>
            </h3>
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              Active Tenant
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Organization Name</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-sm">{activeOrgName || "Silvestre Nosara"}</span>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Organization ID</span>
              <code className="font-mono text-stone-800 dark:text-stone-200 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded text-xs">
                {activeOrgId || "org_silvestre_hotel"}
              </code>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Operator Identity</span>
              <span className="font-medium text-stone-800 dark:text-stone-200">{user?.email || "operator@silvestre.cr"}</span>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Assigned Role</span>
              <span className="font-semibold text-stone-900 dark:text-stone-100">{role || "Admin"}</span>
            </div>
          </div>
        </div>

        {/* Google Workspace & Gmail Integration Card */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800/80 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
              <Mail className="w-4 h-4 text-rose-600 dark:text-rose-400" />
              <span>Google Workspace & Gmail Integration</span>
            </h3>
            {googleIntegration?.status === "active" ? (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                Connected & Active
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                AI Mailbox Sync
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1 text-xs">
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-sm block">
                {googleIntegration?.status === "active"
                  ? `Connected as ${googleIntegration.email}`
                  : "Connect Operator Gmail"}
              </span>
              <p className="text-stone-500 dark:text-stone-400 max-w-lg">
                {googleIntegration?.status === "active"
                  ? "Offline Gmail access (gmail.modify) is active. The AI Concierge can draft responses directly into your mailbox."
                  : `Authorize offline Gmail access (gmail.modify) for your account (${user?.email || "operator@silvestre.cr"}). This enables the AI Concierge to draft responses directly into your mailbox.`}
              </p>
            </div>

            <a
              href={`https://ai.silvestre.cr/auth/google/login?org_id=${encodeURIComponent(activeOrgId || "silvestre")}&user_id=${encodeURIComponent(user?.email || "operator@silvestre.cr")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 font-semibold text-xs hover:bg-stone-800 dark:hover:bg-white transition shrink-0 shadow-2xs"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{googleIntegration?.status === "active" ? "Reconnect Gmail" : "Connect Gmail"}</span>
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          </div>
        </div>

        {/* Xero Accounting Integration Card */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800/80 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
              <FileSpreadsheet className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              <span>Xero Accounting Integration</span>
            </h3>
            {xeroIntegration?.status === "active" ? (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                Connected & Active
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
                Invoicing & Ledger
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1 text-xs">
              <span className="font-semibold text-stone-900 dark:text-stone-100 text-sm block">
                {xeroIntegration?.status === "active"
                  ? `Connected Organization: ${xeroIntegration.tenant_name || xeroIntegration.tenant_id}`
                  : "Connect Xero Organization"}
              </span>
              <p className="text-stone-500 dark:text-stone-400 max-w-lg">
                {xeroIntegration?.status === "active"
                  ? "Offline access for accounting transactions and contacts is active. Knowledge agents can generate invoices and itemized line items."
                  : `Authorize Xero organization access for tenant (${activeOrgName || activeOrgId || "Silvestre"}). Enables automated creation of invoices, bills, and line item sync.`}
              </p>
            </div>

            <a
              href={`https://ai.silvestre.cr/auth/xero/login?org_id=${encodeURIComponent(activeOrgId || "silvestre")}&user_id=${encodeURIComponent(user?.userId || user?.email || "operator@silvestre.cr")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-semibold text-xs transition shrink-0 shadow-2xs"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>{xeroIntegration?.status === "active" ? "Reconnect Xero" : "Connect Xero"}</span>
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          </div>
        </div>

        {/* Backend & pgvector Diagnostics Card */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800/80 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
              <Server className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Service Connectivity & RAG Diagnostics</span>
            </h3>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="capitalize">{status?.status || "Operational"}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Backend Service</span>
              <span className="font-semibold text-stone-800 dark:text-stone-200 font-mono">FastAPI (knowledge-backend)</span>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Vector Index</span>
              <span className="font-semibold text-stone-800 dark:text-stone-200 font-mono">pgvector 18 (384 dimensions)</span>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">Local Embedding Engine</span>
              <span className="font-semibold text-stone-800 dark:text-stone-200 font-mono">FastEmbed (bge-small-en-v1.5)</span>
            </div>

            <div className="space-y-1">
              <span className="text-stone-400 dark:text-stone-500 block font-medium">SDK Client</span>
              <span className="font-semibold text-stone-800 dark:text-stone-200 font-mono">silvestre_ai_shared.knowledge</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
